/**
 * Multi-select compacto para a barra de filtros (Popover + checkboxes),
 * no estilo do ClientFilter do billing.
 *
 * `searchable` (o seletor de cliente — centenas de opções): busca sem acento
 * nem caixa, e os marcados sobem para o topo AO ABRIR (fotografado: o que se
 * marca com a lista aberta não pula de lugar). `max`: a API aceita até 15 ids
 * por filtro; depois disso as opções desmarcadas travam, em vez de a tela
 * pedir e receber 422.
 */
import { useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { matchesOption, pinSelected, type FilterOption } from './report-search';

export type MultiOption = FilterOption;

export function MultiFilter({
  options,
  selected,
  onChange,
  allLabel,
  countLabel,
  searchable = false,
  searchPlaceholder = 'Buscar…',
  max,
  ariaLabel,
}: {
  options: MultiOption[];
  selected: number[];
  onChange: (ids: number[]) => void;
  /** Texto quando nada está marcado, ex. "Todas as mesas". */
  allLabel: string;
  /** Substantivo do contador, ex. (n) => `${n} mesas`. */
  countLabel: (n: number) => string;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** teto de marcados (o da API) */
  max?: number;
  /** nome acessível do botão (o rótulo visual fica fora dele) */
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pinned, setPinned] = useState<number[]>([]);

  function onOpenChange(next: boolean) {
    if (next) {
      setPinned(selected);
      setQuery('');
    }
    setOpen(next);
  }

  function toggle(id: number, checked: boolean) {
    onChange(checked ? [...selected, id] : selected.filter((x) => x !== id));
  }

  const full = max !== undefined && selected.length >= max;
  const listed = searchable
    ? pinSelected(options, pinned).filter((o) => matchesOption(o.name, query))
    : options;

  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={ariaLabel}
            className={cn(
              'flex h-8 w-48 items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 text-[13px] shadow-xs outline-none transition-[color,box-shadow]',
              'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50'
            )}
          >
            <span className={cn('truncate', selected.length === 0 && 'text-muted-foreground')}>
              {selected.length === 0 ? allLabel : countLabel(selected.length)}
            </span>
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className={cn('p-2', searchable ? 'w-80' : 'w-64')}>
          {searchable ? (
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="mb-2 h-8 text-[13px]"
            />
          ) : null}
          <div className="max-h-64 overflow-y-auto">
            {listed.length === 0 ? (
              <p className="px-2 py-3 text-center text-[13px] text-muted-foreground">
                {options.length === 0 ? 'Nada para selecionar' : 'Nenhum resultado'}
              </p>
            ) : (
              listed.map((opt) => {
                const checked = selected.includes(opt.id);
                return (
                  <label
                    key={opt.id}
                    className={cn(
                      'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px] transition-colors',
                      full && !checked
                        ? 'cursor-not-allowed opacity-50'
                        : 'cursor-pointer hover:bg-muted/60'
                    )}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={full && !checked}
                      onCheckedChange={(c) => toggle(opt.id, c === true)}
                    />
                    <span className="truncate" title={opt.name}>
                      {opt.name}
                    </span>
                  </label>
                );
              })
            )}
          </div>
          {full ? (
            <p className="border-t border-divider px-2 pt-2 text-[12px] text-muted-foreground">
              Até {max} por vez — desmarque um para escolher outro.
            </p>
          ) : null}
        </PopoverContent>
      </Popover>
      {selected.length > 0 ? (
        <button
          type="button"
          onClick={() => onChange([])}
          aria-label={`Limpar filtro (${allLabel})`}
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      ) : null}
    </div>
  );
}
